'use client';

import React, { createContext, ReactNode, useContext, useState } from 'react';

interface ModalData {
  isOpen: boolean;
  data?: Record<string, unknown>;
}

interface ModalManagerContextType {
  openModal: (key: string, data?: Record<string, unknown>) => void;
  closeModal: (key: string) => void;
  isModalOpen: (key: string) => boolean;
  getModalData: <T>(key: string) => T | undefined;
}

const ModalManagerContext = createContext<ModalManagerContextType | undefined>(undefined);

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error('useModalManager must be used within a ModalManagerContextProvider');
  }
  return context;
};

export const ModalManagerContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<{ [key: string]: ModalData }>({});

  const openModal = (key: string, data?: Record<string, unknown>) => {
    setModals((prev) => ({
      ...prev,
      [key]: { isOpen: true, data },
    }));
  };

  const closeModal = (key: string) => {
    setModals((prev) => ({
      ...prev,
      [key]: { ...prev[key], isOpen: false },
    }));
  };

  const isModalOpen = (key: string) => {
    return !!modals[key]?.isOpen;
  };

  const getModalData = <T,>(key: string): T | undefined => {
    return modals[key]?.data as T | undefined;
  };

  return (
    <ModalManagerContext.Provider value={{ openModal, closeModal, isModalOpen, getModalData }}>
      {children}
    </ModalManagerContext.Provider>
  );
};
